import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { shimmerText } from './activityShimmer';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  ContentItem,
  ToolActionContentItem,
  CodeExecutionContentItem,
} from '../../shared/interfaces';
import ToolActionBlock from './ToolActionBlock';
import CodeExecutionBlock from './CodeExecutionBlock';

interface Props {
  /** The pre-answer burst, in stream order (see partitionThinkingBurst). */
  items: ContentItem[];
  /** True while the model is still thinking / running the pre-answer tools. */
  pending: boolean;
  /** The user stopped the generation while this group was the live activity:
      label it as interrupted instead of looking like a completed turn. */
  interrupted?: boolean;
}

/**
 * One collapsed, expandable "Pensando… / Pensou" line that aggregates the
 * whole pre-answer phase: the reasoning draft (streaming token-by-token) plus
 * every tool/code step that ran before the first visible answer text. Keeps a
 * turn with 3-4 pre-answer calls to a single line instead of a pile of boxes.
 */
function ThinkingGroup({ items, pending, interrupted }: Props) {
  const [open, setOpen] = useState(false);

  const reasoningText = items
    .filter((it: any) => it.type === 'reasoning')
    .map((it: any) => (it.reasoning || '') as string)
    .filter((t) => t.trim())
    .join('\n\n');
  const steps = items.filter(
    (it: any) => it.type === 'tool_action' || it.type === 'code_execution',
  );
  if (!reasoningText && steps.length === 0) return null;

  return (
    <Row>
      <Header onClick={() => setOpen((v) => !v)}>
        <IconWrap>
          <PsychologyIcon style={{ fontSize: 15 }} />
        </IconWrap>
        <Verb $pending={pending}>
          {pending ? 'Pensando' : interrupted ? 'Pensou · interrompido' : 'Pensou'}
        </Verb>
        {steps.length > 0 && <Counter>{steps.length}</Counter>}
        <ChevronWrap>
          {open ? (
            <ExpandLessIcon style={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon style={{ fontSize: 16 }} />
          )}
        </ChevronWrap>
      </Header>
      {open && (
        <Body>
          {/* Render reasoning and tool/code steps IN STREAM ORDER, so a tool the
              model called mid-thought shows WHERE it happened (reasoning → tool →
              reasoning) instead of every tool being lumped after all the text.
              Adjacent reasoning items coalesce into one paragraph block. */}
          {(() => {
            const out: React.ReactNode[] = [];
            let buf: string[] = [];
            const flushBuf = (k: number) => {
              const txt = buf.join('\n\n').trim();
              if (txt) out.push(<Draft key={`r-${k}`}>{txt}</Draft>);
              buf = [];
            };
            items.forEach((it: any, i) => {
              if (it.type === 'reasoning') {
                if (((it.reasoning || '') as string).trim()) buf.push(it.reasoning as string);
              } else if (it.type === 'tool_action') {
                flushBuf(i);
                out.push(<ToolActionBlock key={i} data={it as ToolActionContentItem} />);
              } else if (it.type === 'code_execution') {
                flushBuf(i);
                out.push(<CodeExecutionBlock key={i} data={it as CodeExecutionContentItem} />);
              }
            });
            flushBuf(items.length);
            return out;
          })()}
        </Body>
      )}
    </Row>
  );
}

export default ThinkingGroup;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  /* Symmetric padding so the hover/background pill doesn't touch the icon;
     the negative margin keeps the icon glyph aligned with the text column. */
  padding: 4px 8px;
  margin-left: -8px;
  cursor: pointer;
  user-select: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  font-size: 0.88rem;
  font-weight: 500;
  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'};
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.85;
  /* Pull the icon glyph optically flush with the message text column: the MUI
     SVG carries ~3px of internal whitespace at this font size. */
  margin-left: -3px;
`;

const Verb = styled.span<{ $pending: boolean }>`
  font-weight: 500;
  ${({ $pending }) => ($pending ? shimmerText : css``)}
`;

const Counter = styled.span`
  font-size: 0.75rem;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 9px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
`;

const Body = styled.div`
  margin: 2px 0 8px 22px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Draft = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  opacity: 0.85;
  margin-bottom: 4px;
`;
